import { useInfiniteQuery } from "@tanstack/react-query";
import { apiClient } from "~/lib/orpc-client";
import { Button } from "~/components/ui/button";
import { useNavigate, useSearch } from "@tanstack/react-router";
import ApplicationsTableView from "./ApplicationsTableView";
import ApplicationsList from "./applications-list";
import { QUERY_KEYS } from "~/constants/queryKeys";
import { Spinner } from "../ui/spinner";
import { useIsMobile } from "~/hooks/use-mobile";
import { useMyOrganization } from "~/hooks/useOrganization";
import { useAuthenticatedUser } from "~/hooks/useAuthenticatedUser";
import { useTenantPath } from "~/hooks/use-tenant-path";
import { useDebounce } from "~/hooks/useDebounce";
import { Search, X } from "lucide-react";
import { useState, useEffect, useCallback, useRef } from "react";
import { MultiSelect } from "~/components/ui/multi-select";

export type ApplicationsTableProps = {
  isCollaboratedView?: boolean;
};

export type StatusOption = {
  value: string;
  label: string;
}

export const STATUS_OPTIONS: Array<StatusOption> = [
  { value: "IN_PROGRESS", label: "In Progress" },
  { value: "WAITING_FOR_COLLABORATOR", label: "Pending Collaborators" },
  { value: "PENDING_IDENTITY_VERIFICATION", label: "Pending Verification" },
  { value: "UNDER_REVIEW", label: "Under Review" },
  { value: "APPROVED", label: "Approved" },
  { value: "REJECTED", label: "Cannot Be Certified" },
  { value: "APPEALED", label: "Appealed" },
];

export const ALL_STATUS_VALUES = STATUS_OPTIONS.map((o: StatusOption) => o.value);
const ITEMS_PER_PAGE = 10;

export default function ApplicationsView({
  isCollaboratedView = false,
}: ApplicationsTableProps) {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { organization } = useMyOrganization();
  const { user, isPublisherAccount, isUniversityAccount, capabilities } =
    useAuthenticatedUser();
  const tenantPath = useTenantPath();
  const effectiveIsOrgMember =
    isPublisherAccount && !!capabilities?.canViewOrganizationApplications;
  const observerRef = useRef<IntersectionObserver | null>(null);
  const hasLoadedOnce = useRef(false);
  const lastTotalCountRef = useRef(0);

  const search = useSearch({ strict: false });

  // Filter state from URL (dashboard only) — university users always see personal scope
  const scope = isUniversityAccount
    ? "mine"
    : !isCollaboratedView && "scope" in search
      ? (search as { scope?: string }).scope || "mine"
      : "mine";
  const urlSearch =
    !isCollaboratedView && "search" in search
      ? (search as { search?: string }).search || ""
      : "";
  const statusFilterRaw =
    !isCollaboratedView && "status" in search
      ? (search as { status?: string }).status ?? undefined
      : undefined;
  const selectedStatuses = statusFilterRaw === undefined
    ? ALL_STATUS_VALUES
    : statusFilterRaw === "none"
      ? []
      : statusFilterRaw.split(",").filter(Boolean);
  const statusFilter = statusFilterRaw === undefined
    ? ""
    : statusFilterRaw === "none"
      ? "none"
      : selectedStatuses.join(",");
  type SortField = "title" | "category" | "status" | "updatedAt" | "owner";
  type SortOrder = "asc" | "desc";
  const sortField =
    !isCollaboratedView && "sort" in search
      ? ((search as { sort?: SortField }).sort || "updatedAt")
      : "updatedAt";
  const sortOrder =
    !isCollaboratedView && "order" in search
      ? ((search as { order?: SortOrder }).order || "desc")
      : "desc";

  // Local search input state for debouncing
  const [searchInput, setSearchInput] = useState(urlSearch);
  const debouncedSearch = useDebounce(searchInput, 400);

  // Read live URL params to avoid stale closure values during rapid interactions
  const getLiveSearch = () => {
    const params = new URLSearchParams(window.location.search);
    return {
      scope: (params.get("scope") || "mine") as "mine" | "organization",
      search: params.get("search") || "",
      status: params.get("status") ?? "",
      sort: (params.get("sort") || "updatedAt") as SortField,
      order: (params.get("order") || "desc") as SortOrder,
    };
  };

  // Sync debounced search to URL
  useEffect(() => {
    if (isCollaboratedView) return;
    if (debouncedSearch === urlSearch) return;

    const live = getLiveSearch();
    navigate({
      to: tenantPath("/dashboard"),
      search: {
        scope: live.scope === "mine" ? undefined : live.scope,
        search: debouncedSearch || undefined,
        status: live.status || undefined,
        sort: live.sort,
        order: live.order,
      },
      replace: true,
    });
  }, [debouncedSearch]);

  // Sync URL search back to input when navigating
  useEffect(() => {
    setSearchInput(urlSearch);
  }, [urlSearch]);

  const updateFilters = (updates: {
    scope?: string;
    status?: string;
    sort?: SortField | "";
    order?: SortOrder | "";
  }) => {
    const live = getLiveSearch();
    const newScope = updates.scope ?? live.scope;
    const newStatus = updates.status ?? live.status;
    const newSort = updates.sort !== undefined
      ? (updates.sort || undefined)
      : live.sort;
    const newOrder = updates.order !== undefined
      ? (updates.order || undefined)
      : live.order;
    navigate({
      to: tenantPath("/dashboard"),
      search: {
        scope: newScope === "mine" ? undefined : (newScope as "mine" | "organization"),
        search: searchInput || undefined,
        status: newStatus || undefined,
        sort: newSort as SortField | undefined,
        order: newOrder as SortOrder | undefined,
      },
    });
  };

  const queryKey = isCollaboratedView
    ? ["collaborations", "list", ITEMS_PER_PAGE]
    : [
      ...QUERY_KEYS.APPLICATIONS_LIST,
      ITEMS_PER_PAGE,
      scope,
      debouncedSearch,
      statusFilter,
      sortField,
      sortOrder,
    ];

  const {
    data,
    isLoading,
    error,
    isFetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey,
    queryFn: async ({ pageParam }) => {
      if (isCollaboratedView) {
        return apiClient.applications.listCollaborated({
          page: pageParam.toString(),
          limit: ITEMS_PER_PAGE.toString(),
        });
      }

      return apiClient.applications.list({
        page: pageParam.toString(),
        limit: ITEMS_PER_PAGE.toString(),
        scope: scope as "mine" | "organization",
        search: debouncedSearch || undefined,
        status: statusFilter || undefined,
        sort: sortField,
        order: sortOrder,
      });
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const pagination = lastPage?.pagination;
      if (!pagination) return undefined;
      return pagination.page < pagination.totalPages
        ? pagination.page + 1
        : undefined;
    },
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: 30000,
  });

  const applications = data?.pages.flatMap((p) => p?.applications ?? []) ?? [];
  const totalCount = data?.pages[0]?.pagination?.total ?? 0;

  if (!isLoading && data) {
    hasLoadedOnce.current = true;
    lastTotalCountRef.current = totalCount;
  }

  // Show full-page spinner only on first ever load
  const showFullSpinner = isLoading && !hasLoadedOnce.current;
  // Show inline loading on table when refetching after filter/sort change
  const isRefetching = isLoading && hasLoadedOnce.current;
  // Use last known count during refetch so it doesn't flash "0 found"
  const displayTotalCount = isRefetching ? lastTotalCountRef.current : totalCount;

  // IntersectionObserver for infinite scroll
  const loadMoreRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (observerRef.current) observerRef.current.disconnect();
      if (!node) return;

      observerRef.current = new IntersectionObserver(
        (entries) => {
          if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) {
            fetchNextPage();
          }
        },
        { rootMargin: "200px" }
      );
      observerRef.current.observe(node);
    },
    [hasNextPage, isFetchingNextPage, fetchNextPage]
  );

  const hasActiveFilters = debouncedSearch || statusFilter;
  const isOrgScope = scope === "organization";
  const showOwnerColumn = effectiveIsOrgMember;

  // Dynamic heading and subtitle based on scope
  const heading = isCollaboratedView
    ? "My Collaborations"
    : isOrgScope && effectiveIsOrgMember
      ? `All ${organization?.displayName || "Organization"} Applications`
      : "My Applications";

  const subtitle = isCollaboratedView
    ? "Applications where you have been invited to be a collaborator"
    : isOrgScope
      ? "All applications across your organization"
      : "Applications you have submitted for certification";

  // Filter bar (dashboard only, not for collaborations view, org members only)
  const filterBar = !isCollaboratedView && effectiveIsOrgMember && (
    <div className="flex flex-col sm:flex-row gap-3 mb-4">
      {/* Search input */}
      <div className="relative flex-1 min-w-0">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <input
          type="text"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Search by title, ISBN, or author..."
          className="w-full h-10 pl-9 pr-9 text-sm border border-border rounded-lg bg-card focus:outline-none focus:ring-2 focus:ring-inset focus:ring-brand-green-primary focus:border-transparent"
        />
        {searchInput && (
          <button
            type="button"
            onClick={() => setSearchInput("")}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Status filter */}
      <MultiSelect
        options={STATUS_OPTIONS}
        selected={selectedStatuses}
        onChange={(values) => {
          const status = values.length === ALL_STATUS_VALUES.length
            ? ""
            : values.length === 0
              ? "none"
              : values.join(",");
          updateFilters({ status });
        }}
        placeholder="Status"
        allSelectedLabel="Status"
        showAllToggle
        allToggleLabel="All"
        className="w-44 shrink-0"
      />

      {/* Org scope toggle */}
      {effectiveIsOrgMember && (
        <label className="flex items-center gap-2 shrink-0 cursor-pointer select-none">
          <span className="text-sm text-foreground">
            Show {organization?.displayName || "organization"}
          </span>
          <button
            type="button"
            role="switch"
            aria-checked={isOrgScope}
            onClick={() =>
              updateFilters({ scope: isOrgScope ? "mine" : "organization" })
            }
            className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${isOrgScope ? "bg-brand-green-primary" : "bg-primary/20"
              }`}
          >
            <span
              className={`inline-block h-3.5 w-3.5 rounded-full bg-white transition-transform ${isOrgScope ? "translate-x-[18px]" : "translate-x-[3px]"
                }`}
            />
          </button>
        </label>
      )}
    </div>
  );

  if (showFullSpinner) {
    return (
      <div className="w-full overflow-hidden">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-4">
          <div className="min-w-0">
            <h2 className="text-2xl font-bold text-foreground truncate">
              {heading}
            </h2>
            <p className="text-sm text-foreground mt-1">{subtitle}</p>
          </div>
        </div>
        {filterBar}
        <div className="bg-card rounded-xl shadow-sm border border-border p-6">
          <div className="flex items-center justify-center py-16">
            <Spinner className="size-8 text-brand-green-primary" />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full overflow-hidden">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-4">
          <div className="min-w-0">
            <h2 className="text-2xl font-bold text-foreground truncate">
              {heading}
            </h2>
            <p className="text-sm text-foreground mt-1">{subtitle}</p>
          </div>
        </div>
        {filterBar}
        <div className="bg-card rounded-xl shadow-sm border border-border p-6">
          <div className="text-destructive text-center py-16">
            <p className="font-medium">
              Error loading{" "}
              {isCollaboratedView ? "collaborations" : "applications"}
            </p>
            <p className="text-sm mt-1">{error.message}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full min-w-0 overflow-hidden">
      {/* Header */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-4">
        <div className="min-w-0">
          <h2 className="text-2xl font-bold text-foreground truncate">
            {heading}
          </h2>
          <p className="text-sm text-foreground mt-1">{subtitle}</p>
        </div>
        <div className="flex py-2 md:py-0 md:justify-end">
          {!isCollaboratedView && (
            <Button
              onClick={() =>
                navigate({
                  to: tenantPath("/apply/$applicationId"),
                  params: { applicationId: "new" },
                })
              }
              size={isMobile ? "sm" : "lg"}
              className="shrink-0"
            >
              + New Application
            </Button>
          )}
        </div>
      </div>

      {filterBar}

      {/* Thesis submission limit banner for university accounts */}
      {isUniversityAccount && user?.thesisSubmissionCount && (
        <div className="mb-4 p-4 bg-muted rounded-xl border border-border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-foreground">
                Essay or Thesis Submissions in {new Date().getFullYear()}
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">
                {user.thesisSubmissionCount.used} of{" "}
                {user.thesisSubmissionCount.limit} submissions used (
                {user.thesisSubmissionCount.limit -
                  user.thesisSubmissionCount.used}{" "}
                remaining)
              </p>
            </div>
            <div className="text-2xl font-bold text-primary">
              {user.thesisSubmissionCount.used}/{user.thesisSubmissionCount.limit}
            </div>
          </div>
          <div className="mt-2 h-2 bg-border rounded-full overflow-hidden">
            <div
              className="h-full bg-primary rounded-full transition-all"
              style={{
                width: `${(user.thesisSubmissionCount.used / user.thesisSubmissionCount.limit) * 100}%`,
              }}
            />
          </div>
        </div>
      )}

      {/* Results count */}
      <div className="flex items-center gap-2 mb-3 h-8">
        <p className="text-sm text-foreground">
          {displayTotalCount}{" "}
          {isCollaboratedView ? "collaboration" : "application"}
          {displayTotalCount !== 1 ? "s" : ""} found
        </p>
        {hasActiveFilters && (
          <button
            type="button"
            onClick={() => {
              setSearchInput("");
              updateFilters({ status: "" });
            }}
            className="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-medium rounded-full bg-brand-green-primary text-white hover:bg-brand-green-primary/80 cursor-pointer transition-colors"
          >
            <X className="w-3 h-3" />
            Clear filters
          </button>
        )}
        {(isRefetching || (isFetching && !isLoading && !isFetchingNextPage)) && (
          <Spinner className="size-4 text-brand-green-primary" />
        )}
      </div>

      {/* Table */}
      <div className="bg-card shadow-sm border border-border w-full min-w-0 hidden md:block rounded-xl">
        <div className="rounded-[calc(theme(borderRadius.xl)-1px)] overflow-hidden">
          <ApplicationsTableView
            applications={applications}
            isCollaboratedView={isCollaboratedView}
            showOwner={showOwnerColumn}
            sort={sortField}
            order={sortOrder}
            loading={isRefetching}
            onSort={(column: SortField) => {
              const live = getLiveSearch();
              if (live.sort === column) {
                updateFilters({ sort: column, order: live.order === "desc" ? "asc" : "desc" });
              } else {
                updateFilters({ sort: column, order: column === "updatedAt" ? "desc" : "asc" });
              }
            }}
          />
        </div>
      </div>
      <div className="block md:hidden">
        {isRefetching ? (
          <div className="flex items-center justify-center py-16">
            <Spinner className="size-6 text-brand-green-primary" />
          </div>
        ) : (
          <ApplicationsList
            applications={applications}
            isCollaboratedView={isCollaboratedView}
          />
        )}
      </div>

      {/* Infinite scroll sentinel + loading indicator */}
      <div ref={loadMoreRef} className="flex items-center justify-center h-12">
        {isFetchingNextPage && (
          <Spinner className="size-6 text-brand-green-primary" />
        )}
      </div>
    </div>
  );
}
